import { useEffect, useState, lazy, Suspense } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { BUILD_VERSION, BUILD_DATE } from '@/buildInfo';

// Print build info to console for easy verification of deployed version
// eslint-disable-next-line no-console
console.log(`[PulpitPilot AI] Build ${BUILD_VERSION} (${BUILD_DATE})`);

// Eager load critical pages
import LandingPage from '@/pages/LandingPage';
import Login from '@/pages/Login';
import Dashboard from '@/pages/Dashboard';

// Lazy load other pages for better performance
const SermonBuilder = lazy(() => import('@/pages/SermonBuilder'));
const SermonDetail = lazy(() => import('@/pages/SermonDetail'));
const CommunityFeed = lazy(() => import('@/pages/CommunityFeed'));
const Community = lazy(() => import('@/pages/Community'));
const Pricing = lazy(() => import('@/pages/Pricing'));
const SeriesBuilder = lazy(() => import('@/pages/SeriesBuilder'));
const VideoStudio = lazy(() => import('@/pages/VideoStudio'));
const VideoEditor = lazy(() => import('@/pages/VideoEditor'));
const BibleReader = lazy(() => import('@/pages/BibleReader'));
const StudyGuide = lazy(() => import('@/pages/StudyGuide'));
const AccountSettings = lazy(() => import('@/pages/AccountSettings'));
const TutorialTest = lazy(() => import('@/pages/TutorialTest'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const AdminDashboard = lazy(() => import('@/pages/AdminDashboard'));
const PreachingMode = lazy(() => import('@/pages/PreachingMode'));
const TeamManagement = lazy(() => import('@/pages/TeamManagement'));
const VoiceCloaking = lazy(() => import('@/pages/VoiceCloaking'));
const Analytics = lazy(() => import('@/pages/Analytics'));

// Homiletics Mentor Pathway (feature-flagged)
const HomileticsLayout = lazy(() => import('@/pages/homiletics/HomileticsLayout'));
const HomileticsOverview = lazy(() => import('@/pages/homiletics/pages').then(m => ({ default: m.OverviewPage })));
const HomileticsWelcome = lazy(() => import('@/pages/homiletics/pages').then(m => ({ default: m.WelcomePage })));
const HomileticsAssessment = lazy(() => import('@/pages/homiletics/AssessmentPlayer'));
const HomileticsCompass = lazy(() => import('@/pages/homiletics/CompassResults'));
const HomileticsMentorRoom = lazy(() => import('@/pages/homiletics/pages').then(m => ({ default: m.MentorRoomPage })));
const HomileticsPathway = lazy(() => import('@/pages/homiletics/curriculum').then(m => ({ default: m.CurriculumPathwayPage })));
const HomileticsModuleDetail = lazy(() => import('@/pages/homiletics/curriculum').then(m => ({ default: m.CurriculumModuleDetailPage })));
const HomileticsLessonPlayer = lazy(() => import('@/pages/homiletics/curriculum').then(m => ({ default: m.LessonPlayerPage })));
const HomileticsFirstSermon = lazy(() => import('@/pages/homiletics/pages').then(m => ({ default: m.FirstSermonPage })));
const HomileticsPracticeLab = lazy(() => import('@/pages/homiletics/pages').then(m => ({ default: m.PracticeLabPage })));
const HomileticsMilestones = lazy(() => import('@/pages/homiletics/pages').then(m => ({ default: m.MilestonesPage })));

// Loading component
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cloud-gray via-white to-glory-50">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-pulpit-600 border-t-transparent mb-4"></div>
        <p className="text-pulpit-900 font-medium">Loading...</p>
      </div>
    </div>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

// Runtime backend-URL rewriter: if the baked-in REACT_APP_BACKEND_URL points to a different
// host than where the app is actually served from (happens when Emergent deployment uses a stale
// custom domain mapping), rewrite API calls to use the current origin. This keeps requests
// same-origin, avoiding cookie/CORS issues in production.
//
// Activates ONLY in the browser, when current origin differs from the baked-in backend URL.
// Safe in preview (same origin) and local dev (different port handled normally).
const _currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
const _shouldRewriteApiHost =
  typeof window !== 'undefined' &&
  BACKEND_URL &&
  !BACKEND_URL.startsWith(_currentOrigin) &&
  // Only rewrite when we're on a plain http(s) page (not file://, not about:blank)
  /^https?:$/.test(window.location.protocol);

axios.interceptors.request.use((config) => {
  // Attach Bearer token from localStorage to every request.
  // This makes auth resilient to third-party cookie blocking (Safari ITP, Chrome 3rd-party cookie deprecation)
  // and avoids the auth-loop bug that occurs when cookies are silently rejected cross-domain.
  const token = localStorage.getItem('session_token');
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization && !config.headers.authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }

  // Rewrite absolute API URLs pointing to stale baked-in backend → current origin.
  if (_shouldRewriteApiHost && config.url && typeof config.url === 'string') {
    if (config.url.startsWith(BACKEND_URL)) {
      config.url = _currentOrigin + config.url.slice(BACKEND_URL.length);
    }
  }
  return config;
});

// Global response interceptor: on 401, clear stale session so user gets cleanly redirected to login
// (prevents infinite 401 loops when token is invalid).
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      const url = error.config?.url || '';
      // Only clear session for auth-critical endpoints, not for every 401 (e.g. tier-gated routes)
      if (url.includes('/auth/me') || url.includes('/auth/session')) {
        localStorage.removeItem('session_token');
        localStorage.removeItem('user');
      }
    }
    return Promise.reject(error);
  }
);

function AuthHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  // Initialize user from localStorage to prevent flash of unauthenticated state
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });

  useEffect(() => {
    const handleAuth = async () => {
      // Check for session_id in URL fragment (Google OAuth callback)
      const hash = window.location.hash;
      if (hash && hash.includes('session_id=')) {
        const sessionId = hash.split('session_id=')[1].split('&')[0];
        
        try {
          const response = await axios.post(`${API}/auth/session`, {}, {
            headers: { 'X-Session-ID': sessionId },
            withCredentials: true
          });
          
          // Store session token for backup authentication
          if (response.data.session_token) {
            localStorage.setItem('session_token', response.data.session_token);
          }
          localStorage.setItem('user', JSON.stringify(response.data.user));
          
          setUser(response.data.user);
          window.history.replaceState({}, document.title, '/dashboard');
          navigate('/dashboard');
        } catch (error) {
          console.error('Auth error:', error);
          navigate('/');
        }
        setLoading(false);
        return;
      }

      // Check existing session - try cookie first, then localStorage token as backup
      try {
        // Build headers - include Authorization as backup for browsers with cookie issues
        const headers = {};
        const storedToken = localStorage.getItem('session_token');
        if (storedToken) {
          headers['Authorization'] = `Bearer ${storedToken}`;
        }
        
        const response = await axios.get(`${API}/auth/me`, {
          withCredentials: true,
          headers: headers
        });
        
        // Update user state and localStorage with fresh data
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
        
        // If on login page or landing page and user is authenticated, go to dashboard
        if (location.pathname === '/' || location.pathname === '/login') {
          navigate('/dashboard');
        }
      } catch (error) {
        // Clear any stale tokens and user data
        localStorage.removeItem('session_token');
        localStorage.removeItem('user');
        setUser(null);
        
        // Only redirect to landing page if user is on a protected route
        const publicRoutes = ['/', '/bible', '/login', '/pricing', '/terms'];
        if (!publicRoutes.includes(location.pathname)) {
          navigate('/');
        }
      }
      setLoading(false);
    };

    handleAuth();
  }, [navigate, location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-cloud-gray via-white to-glory-50">
        <div className="text-2xl font-semibold text-pulpit-800">Loading...</div>
      </div>
    );
  }

  const handleUpgrade = (newTier) => {
    if (user) {
      setUser({ ...user, subscription_tier: newTier });
    }
  };

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/dashboard" element={user ? <Dashboard user={user} setUser={setUser} /> : <Navigate to="/login" />} />
        <Route path="/sermon/new" element={user ? <SermonBuilder user={user} /> : <Navigate to="/" />} />
        <Route path="/sermon/:id" element={user ? <SermonDetail user={user} /> : <Navigate to="/" />} />
        <Route path="/community-old" element={user ? <CommunityFeed user={user} /> : <Navigate to="/" />} />
        <Route path="/community" element={user ? <Community user={user} /> : <Navigate to="/login" />} />
        <Route path="/pricing" element={<Pricing user={user} onUpgrade={handleUpgrade} />} />
        <Route path="/series/new" element={user ? <SeriesBuilder user={user} /> : <Navigate to="/" />} />
        <Route path="/video-studio" element={user ? <VideoStudio user={user} /> : <Navigate to="/" />} />
        <Route path="/video-editor/:id" element={user ? <VideoEditor user={user} /> : <Navigate to="/" />} />
        <Route path="/bible" element={<BibleReader user={user} />} />
        <Route path="/study-guide" element={user ? <StudyGuide user={user} /> : <Navigate to="/" />} />
        <Route path="/account" element={user ? <AccountSettings user={user} setUser={setUser} /> : <Navigate to="/login" />} />
        <Route path="/tutorial-test" element={<TutorialTest />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/admin" element={user?.is_admin ? <AdminDashboard user={user} /> : <Navigate to="/dashboard" />} />
        <Route path="/preach/:id" element={user ? <PreachingMode user={user} /> : <Navigate to="/login" />} />
        <Route path="/team-management" element={user ? <TeamManagement user={user} /> : <Navigate to="/login" />} />
        <Route path="/voice-cloaking" element={user ? <VoiceCloaking user={user} /> : <Navigate to="/login" />} />
        <Route path="/analytics" element={user ? <Analytics user={user} /> : <Navigate to="/login" />} />

        {/* Homiletics Mentor Pathway (feature-flagged) */}
        <Route path="/homiletics-mentor" element={<HomileticsLayout user={user} />}>
          <Route index element={<HomileticsOverview />} />
          <Route path="welcome" element={<HomileticsWelcome />} />
          <Route path="assessment" element={<HomileticsAssessment />} />
          <Route path="compass" element={<HomileticsCompass />} />
          <Route path="mentor-room" element={<HomileticsMentorRoom />} />
          <Route path="pathway" element={<HomileticsPathway />} />
          <Route path="module/:moduleId" element={<HomileticsModuleDetail />} />
          <Route path="module/:moduleId/lesson/:lessonId" element={<HomileticsLessonPlayer />} />
          <Route path="first-sermon" element={<HomileticsFirstSermon />} />
          <Route path="practice-lab" element={<HomileticsPracticeLab />} />
          <Route path="milestones" element={<HomileticsMilestones />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthHandler />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;